// Route-to-SEO metadata configuration
// Used by PageSEO component to set unique title and description per route

export const DEFAULT_TITLE = 'StratPlan - Intelligent Strategic Planning Platform';
export const DEFAULT_DESCRIPTION =
  'Bring your company direction, priorities, people and projects together. Guided strategic planning with AI assistance, from vision to action.';

export interface RouteMetadata {
  title: string;
  description: string;
}

// Route-specific SEO metadata
export const ROUTE_SEO: Record<string, RouteMetadata> = {
  // Public landing pages
  '/': {
    title: 'StratPlan | Intelligent Strategic Planning',
    description:
      'Bring your vision, analysis, priorities, people and projects together. Build with guided frameworks and AI, then put your strategy to work.',
  },
  '/fb': {
    title: 'Turn Strategy Into Execution | StratPlan',
    description:
      'Give your ambition a clear plan. Connect strategic direction, company context and the work your team does next in one workspace.',
  },
  '/start': {
    title: 'Start Your Strategic Plan | StratPlan',
    description:
      'Create a strategic plan your team can act on. Define your direction, explore the options and connect priorities to projects and tasks.',
  },
  '/strategic-planning-software': {
    title: 'Strategic Planning Software For Serious Operators | StratPlan',
    description:
      'Bring vision, business analysis and priorities into one connected plan. Guided frameworks and AI help you turn company context into practical next steps.',
  },
  '/strategy-execution-software': {
    title: 'Strategy Execution Software | Turn Strategy Into Execution | StratPlan',
    description:
      'Connect objectives to strategies, projects and tasks. Make ownership clear and keep the work that matters visible in one execution workspace.',
  },
  '/business-strategy-software': {
    title: 'AI-Powered Business Strategy Software | StratPlan',
    description:
      'Make the decisions that shape your business. Bring company context, proven frameworks and AI assistance into a connected strategic planning process.',
  },
  '/strategic-alignment-software': {
    title: 'Strategic Alignment Software For Growing Teams | StratPlan',
    description:
      'Give people a shared direction. Connect your strategy to team structure, responsibilities, priorities and the projects they own.',
  },
  '/okr-software': {
    title: 'OKR Software That Actually Gets Used | StratPlan',
    description:
      'Give each objective a clear measure of progress. Learn OKR and KPI frameworks, then connect your strategic objectives to practical delivery.',
  },
  '/strategy-management-software': {
    title: 'Strategy Management Software For Leaders | StratPlan',
    description:
      'Keep the direction, decisions and delivery together. Build, review and refine your strategic plan in one shared workspace.',
  },
  '/strategic-planning-tools': {
    title: 'Strategic Planning Tools That Actually Connect | StratPlan',
    description:
      'SWOT, PESTEL, Five Forces, vision and priorities, connected. Use guided frameworks and AI to build a plan that leads to action.',
  },
  '/business-planning-software': {
    title: 'Business Planning Software That Stays Alive | StratPlan',
    description:
      'Build a business plan that guides your next move. Link company direction, market context, strategic priorities and the work ahead.',
  },
  '/strategic-execution-platform': {
    title: 'Strategic Execution Platform For Serious Operators | StratPlan',
    description:
      'Keep delivery connected to the strategy. Organize projects, tasks, subtasks and ownership around the priorities your company chooses.',
  },
  '/corporate-strategy-software': {
    title: 'Corporate Strategy Software For Organisations | StratPlan',
    description:
      'Bring company direction, organizational structure and strategic priorities into one shared view. Give teams clarity on how their work connects.',
  },

  '/advisers': {
    title: 'Find a Strategic Adviser | StratPlan',
    description:
      'Explore approved advisory firms by industry, expertise and location. Meet their teams and find guidance for your strategic planning journey.',
  },

  // Auth pages
  '/sign-in': {
    title: 'Sign In | StratPlan',
    description:
      'Pick up where your strategy left off. Sign in to your connected company workspace, strategic plan and next steps.',
  },
  '/sign-up': {
    title: 'Sign Up | StratPlan',
    description:
      'Give your ambition a clear plan. Create your StratPlan account to connect your direction, priorities, people and projects.',
  },

  // Invitation
  '/invitation': {
    title: 'Accept Invitation | StratPlan',
    description: 'Join your team on StratPlan and collaborate on strategic planning.',
  },

  // Blog
  '/blog': {
    title: 'Blog | StratPlan',
    description:
      'Fresh thinking for your next strategic move. Explore practical ideas and frameworks for shaping direction, choosing priorities and putting plans into action.',
  },

  // Product feature pages
  '/strategic-framework': {
    title: '5-Step Strategic Framework | StratPlan',
    description:
      'One guided journey from vision to action. Connect foundation, external and internal assessment, strategy formulation and execution in five practical steps.',
  },
  '/ai-insights': {
    title: 'AI-Powered Strategic Insights | StratPlan',
    description:
      'Turn company context into draft statements, strategic options and practical next steps. AI helps you explore possibilities; you choose the direction.',
  },
  '/team-collaboration': {
    title: 'Strategic Planning Team Collaboration | StratPlan',
    description:
      'Give your team a shared view of the strategy. Connect people, positions, responsibilities and ownership to the priorities that matter.',
  },
  '/performance-tracking': {
    title: 'Strategic Performance Tracking & KPIs | StratPlan',
    description:
      'See how the plan connects to progress. Review projects, tasks and strategic priorities, then present your plan with custom reports and PDF exports.',
  },

  // Strategy step pages
  '/foundation': {
    title: 'Foundation: Build Your Strategic Core | StratPlan',
    description:
      'Give your business a direction worth following. Define vision, mission, values and measurable objectives before choosing the work ahead.',
  },
  '/external-assessment': {
    title: 'External Assessment: Understand Your Market | StratPlan',
    description:
      'See the forces shaping your next move. Explore customers, competitors, industry pressure and market change with connected strategic frameworks.',
  },
  '/internal-assessment': {
    title: 'Internal Assessment: Know Your Capabilities | StratPlan',
    description:
      'Know what your business can build on. Assess strengths, gaps, capabilities and resources so your strategy reflects the company you have.',
  },
  '/strategy-formulation': {
    title: 'Strategy Formulation: Define Your Path | StratPlan',
    description:
      'Choose where to focus and how to win. Turn your assessments into competitive choices, growth priorities and strategic initiatives.',
  },
  '/execution-monitoring': {
    title: 'Execution & Monitoring: Turn Strategy Into Results | StratPlan',
    description:
      'Turn strategic choices into work that moves. Connect projects, tasks and ownership, then use a review cadence to keep your plan relevant.',
  },

  // Foundation sub-pages
  '/foundation/vision-statement': {
    title: 'How to Write a Vision Statement | Strategic Planning | StratPlan',
    description:
      'Learn how to craft a compelling vision statement that inspires and guides your organization. Examples from top companies, best practices, and expert guidance.',
  },
  '/foundation/mission-statement': {
    title: 'Mission Statement Guide | Purpose & Why You Exist | StratPlan',
    description:
      "Define your organization's purpose with a powerful mission statement. Learn the difference from vision, use Simon Sinek's Golden Circle, and see real examples.",
  },
  '/foundation/core-values': {
    title: 'Core Values: Define What You Stand For | StratPlan',
    description:
      'Identify and implement core values that guide your culture and decisions. Learn from Netflix, Patagonia, and Zappos. Make values actionable.',
  },
  '/foundation/strategic-objectives': {
    title: 'Strategic Objectives & SMART Goals | StratPlan',
    description:
      'Set strategic objectives that bridge vision to execution. Learn the SMART framework, OKRs, and how to cascade goals. Turn strategy into measurable outcomes.',
  },

  // External Assessment sub-pages
  '/external-assessment/porters-five-forces': {
    title: "Porter's Five Forces Analysis | Competitive Strategy | StratPlan",
    description:
      "Analyze industry attractiveness with Porter's Five Forces. Understand competitive dynamics, supplier/buyer power, threats, and rivalry. Strategic frameworks explained.",
  },
  '/external-assessment/pestel-analysis': {
    title: 'PESTEL Analysis Framework | Macro Environment Scanning | StratPlan',
    description:
      'Conduct PESTEL analysis to identify external factors. Political, Economic, Social, Technological, Environmental, and Legal trends for 2026 and beyond.',
  },
  '/external-assessment/market-analysis': {
    title: 'Market Analysis: TAM, SAM, SOM Framework | StratPlan',
    description:
      'Size your market opportunity with TAM/SAM/SOM framework. Learn market sizing, segmentation, growth analysis, and opportunity assessment.',
  },
  '/external-assessment/competitive-positioning': {
    title: 'Competitive Positioning Strategy | Differentiation | StratPlan',
    description:
      'Map your competitive landscape and define your unique position. Learn positioning strategies, perceptual mapping, and how to win your market.',
  },

  // Internal Assessment sub-pages
  '/internal-assessment/swot-analysis': {
    title: 'SWOT Analysis: Strengths, Weaknesses, Opportunities, Threats | StratPlan',
    description:
      'Conduct comprehensive SWOT analysis to understand your strategic position. Turn insights into strategy with prioritization and action planning.',
  },
  '/internal-assessment/core-competencies': {
    title: 'Core Competencies: Build Competitive Moats | StratPlan',
    description:
      'Identify core competencies that drive competitive advantage. Learn Prahalad & Hamel framework, VRIO analysis, and capability development.',
  },
  '/internal-assessment/resource-audit': {
    title: 'Resource Audit: Inventory Your Strategic Assets | StratPlan',
    description:
      'Audit physical, human, financial, and intangible resources. Use VRIO framework to assess strategic value and allocate resources effectively.',
  },

  // Strategy Formulation sub-pages
  '/strategy-formulation/competitive-strategy': {
    title: 'Competitive Strategy: Cost Leadership vs Differentiation | StratPlan',
    description:
      "Choose your competitive strategy with Porter's Generic Strategies. Cost leadership, differentiation, or focus. Blue Ocean vs Red Ocean strategy explained.",
  },
  '/strategy-formulation/growth-strategy': {
    title: 'Growth Strategy: Ansoff Matrix & Scaling Playbook | StratPlan',
    description:
      'Plan your growth with the Ansoff Matrix. Market penetration, development, product innovation, and diversification. Organic vs inorganic growth strategies.',
  },
  '/strategy-formulation/strategic-initiatives': {
    title: 'Strategic Initiatives: Prioritize What Matters | StratPlan',
    description:
      'Select and prioritize strategic initiatives. Learn initiative selection criteria, portfolio management, and how to turn strategy into action.',
  },

  // Execution & Monitoring sub-pages
  '/execution-monitoring/okrs-kpis': {
    title: 'OKRs & KPIs: Measure What Matters | Strategic Metrics | StratPlan',
    description:
      "Set objectives and key results (OKRs) and KPIs to track progress. Learn Google's OKR methodology, leading vs lagging indicators, and metric frameworks.",
  },
  '/execution-monitoring/strategic-cadence': {
    title: 'Strategic Cadence: Annual, Quarterly, Monthly Reviews | StratPlan',
    description:
      'Establish strategic review rhythms. Annual planning, quarterly business reviews, monthly check-ins. Meeting structures and decision frameworks.',
  },
  '/execution-monitoring/continuous-improvement': {
    title: 'Continuous Improvement: PDCA Cycle & Learning Culture | StratPlan',
    description:
      'Build continuous improvement into strategy execution. Plan-Do-Check-Act cycle, retrospectives, pivoting strategies, and learning organizations.',
  },
  '/execution-monitoring/accountability-ownership': {
    title: 'Accountability & Ownership: RACI Matrix & Scorecards | StratPlan',
    description:
      'Assign clear responsibilities with RACI matrices. Performance management, accountability systems, executive scorecards, and ownership culture.',
  },

  // Company pages
  '/about': {
    title: 'About Us | StratPlan',
    description:
      'StratPlan connects ambition and action. Learn why we bring company direction, business analysis, people and delivery into one shared planning workspace.',
  },
  '/health-check': {
    title: 'Free 12-Point Strategy Health Check | StratPlan',
    description:
      'Find the gaps between ambition and execution. Answer twelve practical strategy questions and see which areas deserve your attention next.',
  },
  '/health-check/complete': {
    title: 'Your Strategy Health Report | StratPlan',
    description:
      'Complete your strategy health check and continue into your connected planning workspace.',
  },
  '/health-check/verify': {
    title: 'Verify Your Strategy Health Report | StratPlan',
    description:
      'Verify your email to receive your strategy health check report and review your next steps.',
  },
  '/accept-sign-in': {
    title: 'Continue to Your Workspace | StratPlan',
    description: 'Continue securely into your connected strategic planning workspace.',
  },
  '/careers': {
    title: 'Careers | StratPlan',
    description:
      'Build a product that turns ideas into action. Explore opportunities to make strategic planning clearer, more connected and easier to use.',
  },
  '/contact': {
    title: 'Contact Us | StratPlan',
    description:
      'Make your next move clearer. Ask the StratPlan team about choosing a plan, getting started or using the platform with your company.',
  },
  '/privacy': {
    title: 'Privacy Policy | StratPlan',
    description:
      'Your information, clearly explained. Read how StratPlan collects, uses and protects the information you share, and understand your choices.',
  },
  '/pricing': {
    title: 'Pricing | StratPlan',
    description:
      'Start with Founder: $197 for Year 1, renewing at $997 per year. Explore Pro activation options when you are ready to connect your plan to execution.',
  },

  // GBC Transition
  '/gbc-transition': {
    title: 'Global Business Consultants is now StratPlan',
    description:
      'Global Business Consultants has become StratPlan. Explore a connected workspace for your direction, business context, priorities and delivery.',
  },
  '/transition': {
    title: 'Global Business Consultants is now StratPlan',
    description:
      'Global Business Consultants has become StratPlan. Explore a connected workspace for your direction, business context, priorities and delivery.',
  },

  // 404 fallback
  '*': {
    title: 'Page Not Found | StratPlan',
    description:
      "The page you're looking for doesn't exist. Return to StratPlan to continue your strategic planning journey.",
  },
};

/**
 * Get SEO metadata for a given pathname
 * Handles exact matches, blog post patterns, and 404 fallbacks
 */
export function getRouteMetadata(pathname: string): RouteMetadata {
  // Exact match
  if (ROUTE_SEO[pathname]) {
    return ROUTE_SEO[pathname];
  }

  // Clerk verification and recovery screens share their parent auth page metadata.
  if (pathname.startsWith('/sign-in/')) return ROUTE_SEO['/sign-in'];
  if (pathname.startsWith('/sign-up/')) return ROUTE_SEO['/sign-up'];

  if (pathname.startsWith('/advisers/'))
    return {
      title: 'Strategic Advisory Firm | StratPlan',
      description: 'Meet the firm and team helping businesses turn strategy into practical action.',
    };
  if (pathname === '/adviser' || pathname.startsWith('/adviser/'))
    return { title: 'Adviser Workspace | StratPlan', description: DEFAULT_DESCRIPTION };

  // Blog post pattern: /blog/:slug
  if (pathname.startsWith('/blog/') && pathname.length > 6) {
    // Return generic blog metadata - will be overridden by BlogPostPage once post loads
    return {
      title: 'Blog | StratPlan',
      description: 'Strategic planning insights and best practices from StratPlan.',
    };
  }

  // Protected routes that don't need unique SEO (user is logged in)
  if (
    pathname.startsWith('/dashboard') ||
    pathname.startsWith('/company/') ||
    pathname.startsWith('/user-profile') ||
    pathname.startsWith('/admin')
  ) {
    return {
      title: 'StratPlan',
      description: DEFAULT_DESCRIPTION,
    };
  }

  // 404 fallback for unknown public routes
  return ROUTE_SEO['*'];
}
